import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { useClosePotMutation } from '../../features/pots/queries';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';

const ACTIONS = {
  REVERT_ALL: 'revert_all',
  KEEP_HISTORY: 'keep_history',
};

/**
 * CHANGE v2 §24.3: confirma o fecho do cofre, deixando o utilizador escolher
 * se quer apagar todas as despesas (REVERT_ALL) ou manter o histórico
 * (KEEP_HISTORY).
 */
export function ClosePotDialog({ pot, isOpen, onClose }) {
  const [action, setAction] = useState(ACTIONS.KEEP_HISTORY);
  const closeMutation = useClosePotMutation();

  useEffect(() => {
    if (isOpen) setAction(ACTIONS.KEEP_HISTORY);
  }, [isOpen]);

  if (!pot) return null;

  const submit = (event) => {
    event.preventDefault();
    closeMutation.mutate(
      { id: pot.id, action },
      {
        onSuccess: () => {
          toast.success(pt.pots.close.success);
          onClose();
        },
        onError: (err) => {
          const message =
            (err && (err.detail || JSON.stringify(err))) ||
            'Não foi possível fechar o cofre.';
          toast.error(typeof message === 'string' ? message : 'Erro');
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pt.pots.close.title}</DialogTitle>
          <DialogDescription>{pt.pots.close.subtitle}</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={submit}>
          <fieldset className="flex flex-col gap-3">
            <label className="flex items-start gap-3 rounded-2xl border border-border p-3 hover:border-sage-400">
              <input
                type="radio"
                name="close_action"
                value={ACTIONS.KEEP_HISTORY}
                checked={action === ACTIONS.KEEP_HISTORY}
                onChange={() => setAction(ACTIONS.KEEP_HISTORY)}
                className="mt-1"
              />
              <div className="flex flex-col">
                <span className="font-medium text-foreground">
                  {pt.pots.close.keepHistoryLabel}
                </span>
                <span className="text-xs text-muted-foreground">
                  {pt.pots.close.keepHistoryHelp}
                </span>
              </div>
            </label>

            <label className="flex items-start gap-3 rounded-2xl border border-border p-3 hover:border-error/60">
              <input
                type="radio"
                name="close_action"
                value={ACTIONS.REVERT_ALL}
                checked={action === ACTIONS.REVERT_ALL}
                onChange={() => setAction(ACTIONS.REVERT_ALL)}
                className="mt-1"
              />
              <div className="flex flex-col">
                <span className="font-medium text-foreground">
                  {pt.pots.close.revertAllLabel}
                </span>
                <span className="text-xs text-muted-foreground">
                  {pt.pots.close.revertAllHelp}
                </span>
              </div>
            </label>
          </fieldset>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.pots.close.cancel}
            </Button>
            <Button
              type="submit"
              variant={action === ACTIONS.REVERT_ALL ? 'destructive' : 'primary'}
              disabled={closeMutation.isPending}
            >
              {closeMutation.isPending ? pt.common.loading : pt.pots.close.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
